import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * CoA mapping version entity (matches backend CoAMappingVersionResponse)
 */
export type TCoAVersion = {
    id: string;
    serviceProviderId?: string;
    portfolioCompanyId?: string;
    versionNumber: number;
    sourceType: "upload" | "snapshot";
    storagePath: string | null;
    originalFilename: string | null;
    createdById: string;
    createdByName: string | null;
    mappingsCount: number;
    description: string | null;
    createdAt: string;
};

/**
 * Paginated CoA versions response
 */
export type TPaginatedCoAVersions = {
    items: TCoAVersion[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
};

/**
 * Version upload result (matches backend VersionUploadResponse)
 */
export type TVersionUploadResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    versionId: string | null;
    versionNumber: number | null;
    mappingsCount: number;
    storagePath: string | null;
};

/**
 * Version restore result (matches backend VersionRestoreResponse)
 */
export type TVersionRestoreResult = {
    success: boolean;
    errors: string[];
    warnings: string[];
    mappingsDeleted: number;
    mappingsCreated: number;
};

/**
 * CoA versions slice state (prefixed with coa to avoid collision with propcode versions slice)
 */
export type TCoAVersionsState = {
    coaVersionsListState: TAsyncState<TPaginatedCoAVersions, string>;
    coaVersionSelectedBusinessPartnerId: string | null;
    /** When set, fetchCoAVersions will ensure this version is in the list (merge if API omits it). */
    coaLastUploadedVersion: TCoAVersion | null;
    coaVersionUploadState: TAsyncState<TVersionUploadResult, string>;
    coaSnapshotState: TAsyncState<TVersionUploadResult, string>;
    coaRestoreState: TAsyncState<TVersionRestoreResult, string>;
};

/**
 * CoA versions slice actions
 */
export type TCoAVersionsActions = {
    setCoAVersionsLoading: () => void;
    setCoAVersionsList: (data: TPaginatedCoAVersions) => void;
    setCoAVersionsError: (error: string) => void;
    setCoAVersionSelectedBusinessPartnerId: (businessPartnerId: string | null) => void;
    setCoALastUploadedVersion: (version: TCoAVersion | null) => void;
    setCoAVersionUploadLoading: () => void;
    setCoAVersionUploadSuccess: (result: TVersionUploadResult) => void;
    setCoAVersionUploadError: (error: string) => void;
    resetCoAVersionUploadState: () => void;
    setCoASnapshotLoading: () => void;
    setCoASnapshotSuccess: (result: TVersionUploadResult) => void;
    setCoASnapshotError: (error: string) => void;
    resetCoASnapshotState: () => void;
    setCoARestoreLoading: () => void;
    setCoARestoreSuccess: (result: TVersionRestoreResult) => void;
    setCoARestoreError: (error: string) => void;
    resetCoARestoreState: () => void;
    resetCoAVersionsState: () => void;
};

/**
 * Complete CoA versions slice
 */
export type TCoAVersionsSlice = TCoAVersionsState & TCoAVersionsActions;

/**
 * Initial state
 */
const initialState: TCoAVersionsState = {
    coaVersionsListState: initialAsyncState<TPaginatedCoAVersions, string>(),
    coaVersionSelectedBusinessPartnerId: null,
    coaLastUploadedVersion: null,
    coaVersionUploadState: initialAsyncState<TVersionUploadResult, string>(),
    coaSnapshotState: initialAsyncState<TVersionUploadResult, string>(),
    coaRestoreState: initialAsyncState<TVersionRestoreResult, string>(),
};

/**
 * Create CoA versions slice
 */
export const createSliceCoAVersions = (): TStateCreator<TCoAVersionsSlice> => {
    return (set) => ({
        ...initialState,

        setCoAVersionsLoading: () =>
            set((state) => ({
                coaVersionsListState: pendingAsyncState<TPaginatedCoAVersions, string>(
                    state.coaVersionsListState.data,
                ),
            })),

        setCoAVersionsList: (data) =>
            set(() => ({
                coaVersionsListState: fulfilledAsyncState<TPaginatedCoAVersions, string>(data),
            })),

        setCoAVersionsError: (error) =>
            set((state) => ({
                coaVersionsListState: rejectedAsyncState<TPaginatedCoAVersions, string>(
                    error,
                    state.coaVersionsListState.data,
                ),
            })),

        setCoAVersionSelectedBusinessPartnerId: (businessPartnerId) =>
            set(() => ({
                coaVersionSelectedBusinessPartnerId: businessPartnerId,
            })),

        setCoALastUploadedVersion: (version) =>
            set(() => ({
                coaLastUploadedVersion: version,
            })),

        setCoAVersionUploadLoading: () =>
            set((state) => ({
                coaVersionUploadState: pendingAsyncState<TVersionUploadResult, string>(
                    state.coaVersionUploadState.data,
                ),
            })),

        setCoAVersionUploadSuccess: (result) =>
            set(() => ({
                coaVersionUploadState: fulfilledAsyncState<TVersionUploadResult, string>(result),
            })),

        setCoAVersionUploadError: (error) =>
            set((state) => ({
                coaVersionUploadState: rejectedAsyncState<TVersionUploadResult, string>(
                    error,
                    state.coaVersionUploadState.data,
                ),
            })),

        resetCoAVersionUploadState: () =>
            set(() => ({
                coaVersionUploadState: initialAsyncState<TVersionUploadResult, string>(),
            })),

        setCoASnapshotLoading: () =>
            set((state) => ({
                coaSnapshotState: pendingAsyncState<TVersionUploadResult, string>(
                    state.coaSnapshotState.data,
                ),
            })),

        setCoASnapshotSuccess: (result) =>
            set(() => ({
                coaSnapshotState: fulfilledAsyncState<TVersionUploadResult, string>(result),
            })),

        setCoASnapshotError: (error) =>
            set((state) => ({
                coaSnapshotState: rejectedAsyncState<TVersionUploadResult, string>(
                    error,
                    state.coaSnapshotState.data,
                ),
            })),

        resetCoASnapshotState: () =>
            set(() => ({
                coaSnapshotState: initialAsyncState<TVersionUploadResult, string>(),
            })),

        setCoARestoreLoading: () =>
            set((state) => ({
                coaRestoreState: pendingAsyncState<TVersionRestoreResult, string>(
                    state.coaRestoreState.data,
                ),
            })),

        setCoARestoreSuccess: (result) =>
            set(() => ({
                coaRestoreState: fulfilledAsyncState<TVersionRestoreResult, string>(result),
            })),

        setCoARestoreError: (error) =>
            set((state) => ({
                coaRestoreState: rejectedAsyncState<TVersionRestoreResult, string>(
                    error,
                    state.coaRestoreState.data,
                ),
            })),

        resetCoARestoreState: () =>
            set(() => ({
                coaRestoreState: initialAsyncState<TVersionRestoreResult, string>(),
            })),

        resetCoAVersionsState: () => set(() => initialState),
    });
};
